import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, ChevronRight } from "lucide-react";

// Menu du clic droit, comme dans Discord : fond sombre, survol violet, actions
// dangereuses en rouge, sous-menus au survol (rôles, exclusion…).
//
// items : liste d'objets { label, icon, onClick, danger, disabled, hint, sub,
// checked, color, keep, accent } ; "sep" pour un séparateur ; { header } pour un titre.
// keep : le menu reste ouvert après le clic (cases à cocher des rôles).
// accent : en violet, comme « Inviter des gens » sur Discord.

function place(el, x, y) {
  if (!el) return;
  const r = el.getBoundingClientRect();
  const left = Math.max(8, Math.min(x, window.innerWidth - r.width - 8));
  const top = Math.max(8, Math.min(y, window.innerHeight - r.height - 8));
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}

function Item({ item, onClose, onHover, open }) {
  const ref = useRef(null);
  const Icon = item.icon;
  const hasSub = Array.isArray(item.sub);
  const run = () => {
    if (item.disabled || hasSub) return;
    item.onClick?.();
    if (!item.keep) onClose();
  };
  return (
    <div ref={ref} className="relative" onMouseEnter={() => onHover(hasSub ? ref.current : null)}>
      <button
        type="button"
        onClick={run}
        disabled={item.disabled}
        title={item.disabled ? item.hint : undefined}
        data-testid={item.testId}
        className={`group flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13.5px] leading-5 ${
          item.disabled ? "cursor-not-allowed text-white/30"
            : item.danger ? "text-[#f23f43] hover:bg-[#da373c] hover:text-white"
              : item.accent ? "text-[#949cf7] hover:bg-[#5865f2] hover:text-white"
              : `text-[#dbdee1] hover:bg-[#5865f2] hover:text-white ${open ? "bg-[#5865f2] text-white" : ""}`
        }`}
      >
        {item.color !== undefined && <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: item.color || "#99aab5" }} />}
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.checked !== undefined && (
          <span className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border ${item.checked ? "border-[#5865f2] bg-[#5865f2] text-white group-hover:border-white" : "border-white/40"}`}>
            {item.checked && <Check className="h-3.5 w-3.5" />}
          </span>
        )}
        {hasSub ? <ChevronRight className="h-4 w-4 shrink-0 opacity-70" /> : Icon && <Icon className="h-[17px] w-[17px] shrink-0 opacity-80" />}
      </button>
    </div>
  );
}

function Panel({ items, onClose, testId, style, panelRef }) {
  const [sub, setSub] = useState(null); // { index, anchor }
  const subRef = useRef(null);
  useLayoutEffect(() => {
    if (!sub || !subRef.current) return;
    const a = sub.anchor.getBoundingClientRect();
    const r = subRef.current.getBoundingClientRect();
    const left = a.right + r.width + 6 > window.innerWidth ? a.left - r.width - 6 : a.right + 6;
    place(subRef.current, left, a.top - 6);
  }, [sub]);
  const subItems = sub ? items[sub.index]?.sub : null;
  return (
    <>
      <div ref={panelRef} style={style} data-testid={testId}
        className="fixed z-[200] min-w-[200px] max-w-[260px] rounded-md border border-black/40 bg-[#111214] p-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
        onContextMenu={(e) => e.preventDefault()}>
        {items.map((item, i) => {
          if (item === "sep") return <div key={`s${i}`} className="mx-1 my-1 h-px bg-white/[0.08]" />;
          if (item.header) return <div key={`h${i}`} className="truncate px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-white/40">{item.header}</div>;
          return <Item key={item.label + i} item={item} onClose={onClose} open={sub?.index === i}
            onHover={(anchor) => setSub(anchor && !item.disabled ? { index: i, anchor } : null)} />;
        })}
      </div>
      {subItems && (
        <div ref={subRef} style={{ left: -9999, top: 0 }} data-testid="console-submenu"
          className="fixed z-[201] max-h-[60vh] min-w-[190px] max-w-[260px] overflow-y-auto rounded-md border border-black/40 bg-[#111214] p-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          onMouseLeave={(e) => { if (!e.relatedTarget || !e.relatedTarget.closest?.("[data-testid='console-menu']")) setSub(null); }}>
          {subItems.length === 0 && <div className="px-2 py-1.5 text-[13px] text-white/40">Rien ici</div>}
          {subItems.map((item, i) => (item === "sep"
            ? <div key={`s${i}`} className="mx-1 my-1 h-px bg-white/[0.08]" />
            : <Item key={item.label + i} item={item} onClose={onClose} onHover={() => {}} />))}
        </div>
      )}
    </>
  );
}

export default function ContextMenu({ menu, items, onClose }) {
  const ref = useRef(null);
  useLayoutEffect(() => { if (menu) place(ref.current, menu.x, menu.y); }, [menu]);
  useEffect(() => {
    if (!menu) return undefined;
    const down = (e) => { if (!e.target.closest?.("[data-testid='console-menu'], [data-testid='console-submenu']")) onClose(); };
    const key = (e) => { if (e.key === "Escape") onClose(); };
    const away = () => onClose();
    window.addEventListener("mousedown", down);
    window.addEventListener("keydown", key);
    window.addEventListener("resize", away);
    window.addEventListener("blur", away);
    return () => {
      window.removeEventListener("mousedown", down);
      window.removeEventListener("keydown", key);
      window.removeEventListener("resize", away);
      window.removeEventListener("blur", away);
    };
  }, [menu, onClose]);
  if (!menu || !items?.length) return null;
  return <Panel items={items} onClose={onClose} testId="console-menu" panelRef={ref} style={{ left: menu.x, top: menu.y }} />;
}
